import { useState, useCallback, useMemo } from "react";
import {
    getCameraStatus,
    restartCamera,
    formatSDCard,
    getCameraHealthDetails,
} from "../../../../../../services/vehicleMasterService";
import { vehicleMasterViewConstants } from "../../../../../../Constant";

const INITIAL_RESPONSE_STATE = { status: null, message: "", updatedAt: "" };

export const useCameraOperations = (cameraId, email) => {
    const [cameraHealthDetails, setCameraHealthDetails] = useState(null);
    const [loadingHealthDetails, setLoadingHealthDetails] = useState(false);
    const [cameraRestartResponse, setCameraRestartResponse] = useState(
        INITIAL_RESPONSE_STATE,
    );
    const [sdCardFormatResponse, setSdCardFormatResponse] = useState(
        INITIAL_RESPONSE_STATE,
    );
    const [cameraRestart, setCameraRestart] = useState(false);
    const [sdCardFormat, setSdCardFormat] = useState(false);

    const createResponse = useCallback((status, message = "", createdAt = "") => ({
        status,
        message,
        createdAt,
    }), []);

    const extractMessage = useCallback((response, fallback) => {
        return response?.message || response?.reason || response?.error || fallback;
    }, []);

    const createOperationHandler = useCallback(
        (setLoading, setResponse, operationFn, successMessage, failureMessage) => {
            return async () => {
                setLoading(true);
                setResponse(createResponse(vehicleMasterViewConstants.statusStates.loading));

                try {
                    const response = await operationFn();

                    if (response?.success) {
                        setResponse(
                            createResponse(
                                vehicleMasterViewConstants.statusStates.success,
                                response?.message || successMessage,
                            ),
                        );
                    } else {
                        setResponse(
                            createResponse(
                                vehicleMasterViewConstants.statusStates.failed,
                                response?.message || failureMessage,
                            ),
                        );
                    }
                } catch (error) {
                    setResponse(
                        createResponse(
                            error?.status || vehicleMasterViewConstants.statusStates.error,
                            error?.description || error?.error || failureMessage,
                        ),
                    );
                } finally {
                    setLoading(false);
                }
            };
        },
        [createResponse],
    );

    const fetchCameraStatusData = useCallback(() => {
        setCameraRestartResponse(createResponse(vehicleMasterViewConstants.statusStates.loading));
        setSdCardFormatResponse(createResponse(vehicleMasterViewConstants.statusStates.loading));

        getCameraStatus(cameraId)
            .then((response) => {
                if (!response?.length) {
                    setCameraRestartResponse(createResponse(vehicleMasterViewConstants.messages.noStatus));
                    setSdCardFormatResponse(createResponse(vehicleMasterViewConstants.messages.noStatus));
                    return;
                }

                const restartConfig = response.find((item) => item?.configType === vehicleMasterViewConstants.configTypes.restartCamera);
                const sdCardConfig = response.find((item) => item?.configType === vehicleMasterViewConstants.configTypes.sdCardFormat);

                if (restartConfig) {
                    setCameraRestartResponse(
                        createResponse(restartConfig.configStatus, extractMessage(restartConfig, ""), restartConfig.createdAt),
                    );
                } else {
                    setCameraRestartResponse(createResponse(vehicleMasterViewConstants.messages.noStatus));
                }

                if (sdCardConfig) {
                    setSdCardFormatResponse(
                        createResponse(sdCardConfig.configStatus, extractMessage(sdCardConfig, ""), sdCardConfig.createdAt),
                    );
                } else {
                    setSdCardFormatResponse(createResponse(vehicleMasterViewConstants.messages.noStatus));
                }
            })
            .catch(() => {
                setCameraRestartResponse(createResponse(vehicleMasterViewConstants.statusStates.error));
                setSdCardFormatResponse(createResponse(vehicleMasterViewConstants.statusStates.error));
            });
    }, [cameraId, createResponse, extractMessage]);

    const fetchCameraHealthDetailsData = useCallback(() => {
        setLoadingHealthDetails(true);

        getCameraHealthDetails(cameraId)
            .then((res) => {
                setCameraHealthDetails(res.data || {});
                setLoadingHealthDetails(false);
            })
            .catch(() => {
                setCameraHealthDetails(null);
                setLoadingHealthDetails(false);
            });
    }, [cameraId]);

    const handleCameraRestart = useCallback(
        createOperationHandler(
            setCameraRestart,
            setCameraRestartResponse,
            () => restartCamera(cameraId, email),
            vehicleMasterViewConstants.messages.cameraRestartSuccess,
            vehicleMasterViewConstants.messages.cameraRestartFailure,
        ),
        [cameraId, email, createOperationHandler],
    );

    const handleSDCardFormat = useCallback(
        createOperationHandler(
            setSdCardFormat,
            setSdCardFormatResponse,
            () => formatSDCard(cameraId, email),
            vehicleMasterViewConstants.messages.sdCardFormatSuccess,
            vehicleMasterViewConstants.messages.sdCardFormatFailure,
        ),
        [cameraId, email, createOperationHandler],
    );

    return useMemo(
        () => ({
            cameraHealthDetails,
            loadingHealthDetails,
            cameraRestartResponse,
            sdCardFormatResponse,
            cameraRestart,
            sdCardFormat,
            fetchCameraStatusData,
            fetchCameraHealthDetailsData,
            handleCameraRestart,
            handleSDCardFormat,
        }),
        [
            cameraHealthDetails,
            loadingHealthDetails,
            cameraRestartResponse,
            sdCardFormatResponse,
            cameraRestart,
            sdCardFormat,
            fetchCameraStatusData,
            fetchCameraHealthDetailsData,
            handleCameraRestart,
            handleSDCardFormat,
        ],
    );
};
